import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, {useEffect, useMemo, useState} from "react";
import TableChartIcon from "@mui/icons-material/TableChart";
import CloudIcon from "@mui/icons-material/Cloud";
import DownloadIcon from "@mui/icons-material/Download";
import Divider from "@mui/material/Divider";

import Title from "../Principal/Title";
import Copyright from "../Principal/Copyright";

import {
  exportExcel,
  exportGoogleSheet,
  getDataset,
  listDatasets,
  runProductPageable,
  runQuery
} from "../../api/dashboardApi";

const MAX_FILTERS_UI = 10;

function buildTsv(columns, rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return s.replace(/\t/g, " ").replace(/\r?\n/g, " ");
  };
  const header = columns.map(esc).join("\t");
  const body = rows.map((r) => columns.map((c) => esc(r[c])).join("\t")).join(
      "\n");
  return `${header}\n${body}`;
}

function opNeedsValue(op) {
  return !["IS_NULL"].includes(op);
}

function opIsIN(op) {
  return op === "IN";
}

function opIsBetween(op) {
  return op === "BETWEEN";
}

export default function DashboardQuery() {
  const [datasets, setDatasets] = useState([]);
  const [datasetId, setDatasetId] = useState("");
  const [meta, setMeta] = useState(null);

  const [filters, setFilters] = useState([]);
  const [sortField, setSortField] = useState("");
  const [sortDir, setSortDir] = useState("ASC");

  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [result, setResult] = useState(null); // QueryResult
  const [selectedCols, setSelectedCols] = useState([]);
  const [colSearch, setColSearch] = useState("");

  const [page, setPage] = useState(0);

  const authHeaders = useMemo(
      () => ({
        "Content-Type": "application/json",
        //Authorization: localStorage.getItem("token"),
      }),
      []
  );

  const allCols = useMemo(() => (meta?.columns ?? []).map((c) => c.name),
      [meta]);

  const visibleCols = useMemo(() => {
    const q = colSearch.trim().toLowerCase();
    if (!q) {
      return allCols;
    }
    return allCols.filter((c) => c.toLowerCase().includes(q));
  }, [allCols, colSearch]);

  const selectedSet = useMemo(() => new Set(selectedCols), [selectedCols]);

  const toggleCol = (col) => {
    setSelectedCols((prev) => {
      const s = new Set(prev);
      if (s.has(col)) {
        s.delete(col);
      } else {
        s.add(col);
      }
      return Array.from(s);
    });
  };

  const selectAllCols = () => setSelectedCols(allCols);
  const clearCols = () => setSelectedCols([]);

  // 1) cargar datasets
  useEffect(() => {
    (async () => {
      try {
        setError("");
        const ds = await listDatasets(authHeaders);
        setDatasets(ds || []);
        if (ds?.length) {
          setDatasetId(ds[0].id);
        }
      } catch (e) {
        setError(e?.message || "No se pudo cargar datasets");
      }
    })();
  }, [authHeaders]);

  // 2) cargar metadata al cambiar dataset
  useEffect(() => {
    if (!datasetId) {
      return;
    }
    (async () => {
      try {
        setError("");
        setMeta(null);
        setResult(null);
        setFilters([]);
        setSortField("");
        setSortDir("ASC");
        setOffset(0);
        setSelectedCols([]);
        setColSearch("");

        const m = await getDataset(datasetId, authHeaders);
        setMeta(m);
        setLimit(Math.min(m?.defaultLimit ?? 50, m?.maxLimit ?? 1000));
      } catch (e) {
        setError(e?.message || "No se pudo cargar metadata");
      }
    })();
  }, [datasetId, authHeaders]);

  const filterable = meta?.filterable ?? [];
  const sortable = meta?.sortable ?? [];

  const addFilter = () => {
    if (!filterable.length) {
      return;
    }
    if (filters.length >= MAX_FILTERS_UI) {
      setError(`Máximo ${MAX_FILTERS_UI} filtros`);
      return;
    }
    const firstField = filterable[0].field;
    const firstOp = filterable[0].ops?.[0] ?? "EQ";
    setFilters([...filters, {field: firstField, op: firstOp, value: ""}]);
  };

  const updateFilter = (idx, patch) => {
    const next = [...filters];
    next[idx] = {...next[idx], ...patch};

    // si cambia field => reset op/value
    if (patch.field) {
      const def = filterable.find((f) => f.field === patch.field);
      next[idx].op = def?.ops?.[0] ?? "EQ";
      next[idx].value = "";
    }

    // si cambia op => reset value
    if (patch.op) {
      next[idx].value = "";
    }

    setFilters(next);
  };

  const removeFilter = (idx) => {
    setFilters(filters.filter((_, i) => i !== idx));
  };

  const normalizeWhere = () => {
    // convierte filtros UI a QueryDsl.WhereClause (tu backend)
    return (filters || [])
    .filter((f) => f.field && f.op)
    .map((f) => {
      const raw = String(f.value ?? "").trim();

      if (f.op === "IS_NULL") {
        return {field: f.field, op: "IS_NULL"};
      }

      if (!raw && opNeedsValue(f.op)) {
        return null;
      }

      if (opIsIN(f.op)) {
        const values = raw.split(",").map((x) => x.trim()).filter(Boolean);
        return values.length ? {field: f.field, op: "IN", values} : null;
      }

      if (opIsBetween(f.op)) {
        const [from, to] = raw.split(",").map((x) => x.trim());
        if (!from || !to) {
          return null;
        }
        return {field: f.field, op: "BETWEEN", from, to};
      }

      // EQ/GTE/LTE/LIKE
      return {field: f.field, op: f.op, value: raw};
    })
    .filter(Boolean);
  };

  const buildPayload = () => {
    const where = normalizeWhere();

    const sort =
        sortField && sortable.includes(sortField)
            ? {field: sortField, dir: sortDir === "DESC" ? "DESC" : "ASC"}
            : null;

    const l = Math.max(1, Math.min(Number(limit || 1), meta?.maxLimit ?? 1000));
    const o = Math.max(0, Number(offset || 0));

    return {
      datasetId,
      select: selectedCols,
      where: where.length ? where : undefined,
      sort: sort ?? undefined,
      limit: l,
      offset: o,
      // select: undefined (por ahora devolvemos todas)
    };
  };

  const execute = async () => {
    try {
      if (!datasetId) {
        return;
      }
      setLoading(true);
      setError("");

      const payload = buildPayload();
      console.log("Payload enviado:", payload);
      let resp;
      if (datasetId === "mobisite_products") {
        resp = await runProductPageable(payload, authHeaders);
      } else {
        resp = await runQuery(payload, authHeaders);
      }
      setResult(resp);
      setTotalRows(resp.totalRows ?? resp.returnedRows ?? resp.rows.length);
      setPage(Math.floor(offset / limit));

    } catch (e) {
      console.error(e);
      setResult(null);
      setError(e?.response?.data || e?.message || "Error ejecutando query");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setOffset(0);

    // Si ya estamos en la primera página,
    // el useEffect no se ejecutará porque offset no cambia.
    if (offset === 0) {
      execute();
    }
  };

  const handleChangePage = (event, newPage) => {
    // Pagination es 1-indexed, convertir a 0-indexed
    const pageIndex = newPage - 1;
    setPage(pageIndex);
    const newOffset = pageIndex * limit;
    setOffset(newOffset);
  };

  // Handler para el Select del nuevo componente de paginación
  const handleSelectRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setLimit(newRowsPerPage);
    setOffset(0);
    setPage(0);
  };

  // 3) ejecutar query cuando cambian limit u offset (paginación)
  useEffect(() => {
    if (!datasetId) return;

    execute();

  }, [datasetId, limit, offset]);

  const copyTsv = async () => {
    if (!result) {
      return;
    }
    const tsv = buildTsv(result.columns, result.rows);
    await navigator.clipboard.writeText(tsv);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExcel = async () => {
    try {
      setLoading(true);
      const payload = buildPayload();

      const response = await exportExcel(
          payload,
          authHeaders
      );

      // Crear blob y descargar archivo
      const blob = new Blob(
          [response.data],
          {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          }
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dashboard-${datasetId}-${new Date().getTime()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      handleClose();
    } catch (e) {
      setError(e?.response?.data || e?.message || "Error exportando Excel");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSheet = async () => {
    try {
      setLoading(true);
      const payload = buildPayload();

      const response = await exportGoogleSheet(
          payload,
          authHeaders
      );

      window.open(
          response.sheetUrl,
          "_blank"
      );

      handleClose();
    } catch (e) {
      setError(
          e?.response?.data || e?.message || "Error generando Google Sheet");
    } finally {
      setLoading(false);
    }
  };

  return (
      <Grid container spacing={2}>
        {/* BARRA SUPERIOR */}
        <Grid item xs={12}>
          <Paper sx={{p: 2, borderRadius: 2}}>
            <Title>Dashboard</Title>

            {error ? (
                <Alert severity="error" sx={{mt: 1}}>
                  <AlertTitle>Error</AlertTitle>
                  {error}
                </Alert>
            ) : null}

            <Stack
                direction={{xs: "column", md: "row"}}
                spacing={2}
                alignItems={{xs: "stretch", md: "center"}}
                sx={{mt: 2}}
            >
              <TextField
                  select
                  label="Dataset"
                  value={datasetId || ""}
                  onChange={(e) => setDatasetId(e.target.value)}
                  fullWidth
                  disabled={!datasets.length}
              >
                {datasets.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.title} ({d.id})
                    </MenuItem>
                ))}
              </TextField>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                    variant="outlined"
                    onClick={() => {
                      setFilters([]);
                      setSortField("");
                      setSortDir("ASC");
                      setResult(null);
                      setSelectedCols([]);
                      setColSearch("");
                    }}
                    disabled={loading}
                >
                  Limpiar
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={!meta || loading}
                >
                  Buscar
                </Button>

                <>
                  <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DownloadIcon/>}
                      onClick={handleClick}
                      disabled={!result || loading}
                  >
                    Exportar
                  </Button>

                  <Menu
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                  >
                    <MenuItem onClick={handleGoogleSheet}>
                      <ListItemIcon>
                        <CloudIcon/>
                      </ListItemIcon>
                      <ListItemText>
                        Generar Google Sheet
                      </ListItemText>
                    </MenuItem>

                    <Divider/>

                    <MenuItem onClick={handleExcel}>
                      <ListItemIcon>
                        <TableChartIcon/>
                      </ListItemIcon>
                      <ListItemText>
                        Descargar Excel
                      </ListItemText>
                    </MenuItem>
                  </Menu>
                </>

              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* PANEL 2: COLUMNAS + FILTROS/ORDEN */}
        <Grid item xs={12}>
          <Paper sx={{p: 2, borderRadius: 2}}>
            <Grid container spacing={2}>
              {/* COLUMNAS */}
              <Grid item xs={12} md={6}>
                <Stack direction="row" alignItems="center"
                       justifyContent="space-between" sx={{mb: 1}}>
                  <Typography fontWeight={600}>Columnas</Typography>

                  <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={selectAllCols}
                        disabled={!meta || loading || allCols.length === 0}
                    >
                      Select all
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={clearCols}
                        disabled={!meta || loading || selectedCols.length === 0}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center"
                       sx={{mb: 1}}>
                  <TextField
                      label="Buscar columna"
                      value={colSearch}
                      onChange={(e) => setColSearch(e.target.value)}
                      fullWidth
                      size="small"
                      disabled={!meta || loading}
                  />
                  <Chip
                      size="small"
                      variant="outlined"
                      label={
                        selectedCols.length === 0
                            ? `Todas (${allCols.length})`
                            : `${selectedCols.length} seleccionadas`
                      }
                  />
                </Stack>

                <Paper variant="outlined" sx={{
                  p: 1,
                  borderRadius: 2,
                  maxHeight: 240,
                  overflowY: "auto"
                }}>
                  {!meta ? (
                      <Typography variant="body2" color="text.secondary">
                        Selecciona un dataset para ver columnas.
                      </Typography>
                  ) : visibleCols.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No hay columnas con ese filtro.
                      </Typography>
                  ) : (
                      <FormGroup>
                        {visibleCols.map((c) => (
                            <FormControlLabel
                                key={c}
                                control={
                                  <Checkbox
                                      checked={selectedSet.has(c)}
                                      onChange={() => toggleCol(c)}
                                      disabled={loading}
                                      size="small"
                                  />
                                }
                                label={c}
                            />
                        ))}
                      </FormGroup>
                  )}
                </Paper>

                <Typography variant="caption" color="text.secondary"
                            sx={{mt: 0.5, display: "block"}}>
                  Si no seleccionas ninguna, el backend devuelve todas las
                  columnas.
                </Typography>
              </Grid>

              {/* FILTROS + ORDEN */}
              <Grid item xs={12} md={6}>
                <Stack direction="row" justifyContent="space-between"
                       alignItems="center" sx={{mb: 1}}>
                  <Typography fontWeight={600}>Filtros</Typography>
                  <Button size="small" onClick={addFilter}
                          disabled={!meta || loading}>
                    Agregar
                  </Button>
                </Stack>

                <Stack spacing={1.2} sx={{mb: 2}}>
                  {!filterable.length ? (
                      <Typography variant="body2" color="text.secondary">
                        Este dataset no tiene filtros configurados.
                      </Typography>
                  ) : null}

                  {filters.map((f, idx) => {
                    const def = filterable.find((x) => x.field === f.field);
                    const ops = def?.ops ?? ["EQ"];
                    const type = def?.type ?? "STRING";

                    const placeholder =
                        f.op === "IN"
                            ? "a,b,c"
                            : f.op === "BETWEEN"
                                ? type === "DATETIME"
                                    ? "2026-01-01T00:00:00,2026-01-31T23:59:59"
                                    : "10,20"
                                : type === "DATETIME"
                                    ? "2026-01-23T12:00:00"
                                    : type === "DECIMAL"
                                        ? "10.5"
                                        : "texto";

                    return (
                        <Paper key={idx} variant="outlined"
                               sx={{p: 1.2, borderRadius: 2}}>
                          <Stack spacing={1}>
                            <Stack direction={{xs: "column", sm: "row"}}
                                   spacing={1}>
                              <TextField
                                  select
                                  label="Campo"
                                  value={f.field}
                                  onChange={(e) => updateFilter(idx,
                                      {field: e.target.value})}
                                  fullWidth
                                  disabled={loading}
                              >
                                {filterable.map((ff) => (
                                    <MenuItem key={ff.field} value={ff.field}>
                                      {ff.field} ({ff.type})
                                    </MenuItem>
                                ))}
                              </TextField>

                              <TextField
                                  select
                                  label="Op"
                                  value={f.op}
                                  onChange={(e) => updateFilter(idx,
                                      {op: e.target.value})}
                                  sx={{minWidth: 140}}
                                  disabled={loading}
                              >
                                {ops.map((op) => (
                                    <MenuItem key={op} value={op}>
                                      {op}
                                    </MenuItem>
                                ))}
                              </TextField>
                            </Stack>

                            {f.op !== "IS_NULL" ? (
                                <TextField
                                    label={
                                      f.op === "BETWEEN"
                                          ? "Valor (from,to)"
                                          : f.op === "IN"
                                              ? "Valores (a,b,c)"
                                              : "Valor"
                                    }
                                    value={f.value}
                                    onChange={(e) => updateFilter(idx,
                                        {value: e.target.value})}
                                    placeholder={placeholder}
                                    fullWidth
                                    disabled={loading}
                                    size="small"
                                />
                            ) : (
                                <Typography variant="caption"
                                            color="text.secondary">
                                  IS_NULL no requiere valor
                                </Typography>
                            )}

                            <Stack direction="row" justifyContent="flex-end">
                              <Button size="small" color="error"
                                      onClick={() => removeFilter(idx)}
                                      disabled={loading}>
                                Quitar
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                    );
                  })}
                </Stack>

                <Divider sx={{my: 1.5}}/>

                <Typography fontWeight={600} sx={{mb: 1}}>
                  Orden
                </Typography>

                <Stack direction={{xs: "column", sm: "row"}} spacing={1}>
                  <TextField
                      select
                      label="Campo"
                      value={sortField}
                      onChange={(e) => setSortField(e.target.value)}
                      fullWidth
                      disabled={!sortable.length || loading}
                  >
                    <MenuItem value="">(none)</MenuItem>
                    {sortable.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                      select
                      label="Dir"
                      value={sortDir}
                      onChange={(e) => setSortDir(e.target.value)}
                      sx={{width: {xs: "100%", sm: 160}}}
                      disabled={!sortField || loading}
                  >
                    <MenuItem value="ASC">ASC</MenuItem>
                    <MenuItem value="DESC">DESC</MenuItem>
                  </TextField>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* TABLA RESULTADOS + CHIPS + TSV */}
        <Grid item xs={12}>
          <Paper sx={{p: 2, borderRadius: 2}}>
            <Stack direction="row" alignItems="center"
                   justifyContent="space-between">
              <Title>Resultados</Title>

              <Stack direction="row" spacing={1} alignItems="center">
                {result ? (
                    <>
                      <Chip size="small" label={`${result.returnedRows} filas`}
                            variant="outlined"/>
                      <Chip size="small" label={`${result.elapsedMs} ms`}
                            variant="outlined"/>
                    </>
                ) : (
                    <Chip size="small" label="Sin ejecutar" variant="outlined"/>
                )}

                <Button size="small" onClick={copyTsv} disabled={!result}>
                  Copy TSV
                </Button>
              </Stack>
            </Stack>

            <Divider sx={{my: 1.5}}/>

            {!result ? (
                <Typography variant="body2" color="text.secondary">
                  Selecciona dataset, define filtros y presiona <b>Buscar</b>.
                </Typography>
            ) : (
                <>

                  <Typography variant="body2" color="text.secondary">
                    Mostrando {offset + 1} - {offset + result.rows.length} de {totalRows} registros
                  </Typography>

                  <TableContainer
                      sx={{
                        maxHeight: 600,
                        overflowX: "auto"
                      }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {result.columns.map((c) => (
                              <TableCell
                                  key={c}
                                  sx={{
                                    fontWeight: 700,
                                    backgroundColor: "#f5f5f5",
                                    whiteSpace: "nowrap"
                                  }}
                              >
                                {c}
                              </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {result.rows.map((r, idx) => (
                            <TableRow key={idx}>
                              {result.columns.map((c) => (
                                  <TableCell
                                      key={c}
                                      sx={{
                                        maxWidth: 250,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                  >
                                    {String(r?.[c] ?? "")}
                                  </TableCell>
                              ))}
                            </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Nuevo componente de paginación mejorado */}
                  <Stack spacing={2} alignItems="center" sx={{ my: 3 }}>
                   {/* Información del resultado */}
                    <Typography variant="body2" color="text.secondary">
                      Mostrando {offset + 1} - {offset + result.rows.length} de {totalRows} registros
                    </Typography>

                   {/* Selector de filas por página */}
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                     <Typography variant="body2">Filas por página:</Typography>
                     <Select
                         value={limit}
                         onChange={handleSelectRowsPerPage}
                         size="small"
                         sx={{ minWidth: 80 }}
                     >
                       <MenuItem value={10}>10</MenuItem>
                       <MenuItem value={30}>30</MenuItem>
                       <MenuItem value={50}>50</MenuItem>
                       <MenuItem value={100}>100</MenuItem>
                       <MenuItem value={200}>200</MenuItem>
                     </Select>
                   </Box>

                   {/* Navegación de páginas */}
                    <Pagination
                        page={page + 1}
                        count={Math.max(1, Math.ceil(totalRows / limit))}
                        onChange={handleChangePage}
                        siblingCount={2}
                        boundaryCount={1}
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                        size="large"
                    />
                  </Stack>
                </>
            )}

            <Copyright/>
          </Paper>
        </Grid>
      </Grid>
  );
}
